// The right-hand panel of the two forms: the card as the app draws it, always on screen.
// Shown inside a phone frame with the real home header above it, because the card's colour
// and weight only read correctly against what actually surrounds it on the device — a card
// floating on a white form looks louder than it is.

import { useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { InfoDot } from '../hiring/HiringForm';
import { DECORATIONS, type DecorationType } from './bannerOptions';
import { labelOnFill, previewSegments, readableAccent, tint } from './appCardMirror';

export function PhonePreview({
  title,
  body,
  accent,
  decoration,
  countdownTo,
  ctaLabel,
  ctaArrow = true,
  secondaryLabel = null,
  appSwitch,
  image,
  hint,
  note,
}: {
  title: string;
  /** As the app gets it, with the fallback already applied. */
  body: string;
  accent: string;
  decoration: DecorationType;
  /** The form's value; '' for none. */
  countdownTo: string;
  ctaLabel: string | null;
  /** The button goes to another screen. An answer does not. */
  ctaArrow?: boolean;
  /** A second, outlined button: Decline. */
  secondaryLabel?: string | null;
  /** On a kind with a button: which app the preview shows. */
  appSwitch?: { value: 'new' | 'older'; onChange: (next: 'new' | 'older') => void };
  image: string | null;
  /** How the card behaves, behind the panel's (i). */
  hint: string;
  /** A line under the phone, when the form has something to add. */
  note?: string;
}) {
  // Cut where the app cuts it (3 lines, AnnouncementCard's COLLAPSED_LINES), with its
  // "Read more" when there is more.
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [bodyCut, setBodyCut] = useState(false);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    setBodyCut(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [body, title]);

  const segments = previewSegments(countdownTo);
  const shownTitle = title.trim() || 'Title';
  const emoji = DECORATIONS.find((d) => d.value === decoration)?.emoji;

  return (
    <aside
      aria-label="How it looks in the app"
      className="flex min-h-0 flex-col overflow-hidden rounded-xl bg-gray-50 px-4 pb-4 pt-3 ring-1 ring-inset ring-gray-200"
    >
      <div className="mb-2 flex h-6 flex-none items-center justify-between whitespace-nowrap text-xs font-semibold uppercase tracking-wide text-gray-400">
        {appSwitch ? (
          <>
            <span className="flex items-center gap-1">
              In the app
              <InfoDot text={hint} />
            </span>
            <span role="group" aria-label="Which app" className="flex rounded-md bg-white p-0.5 ring-1 ring-inset ring-gray-200">
              {(['new', 'older'] as const).map((app) => (
                <button
                  key={app}
                  type="button"
                  aria-pressed={appSwitch.value === app}
                  onClick={() => appSwitch.onChange(app)}
                  className={`rounded px-2 py-px text-[11px] font-semibold normal-case tracking-normal transition-colors ${
                    appSwitch.value === app ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {app === 'new' ? 'New app' : 'Older app'}
                </button>
              ))}
            </span>
          </>
        ) : (
          <>
            <span>How it looks in the app</span>
            <InfoDot text={hint} />
          </>
        )}
      </div>

      <div className="mx-auto flex h-[520px] min-h-0 w-[300px] max-w-full flex-col overflow-hidden rounded-[2rem] border-[10px] border-gray-900 bg-gray-900 shadow-xl">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[22px] bg-[#F4F6F8]">
          {/* The app's gradient home header, so the card is judged in context rather
              than against a blank page. */}
          <div className="flex-none rounded-b-2xl bg-gradient-to-br from-[#1E3A5F] to-[#2C5282] px-4 pb-5 pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-white">Good afternoon</p>
                <p className="text-[10px] text-white/70">
                  {new Date().toLocaleDateString(undefined, {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </p>
              </div>
              <div className="h-8 w-8 rounded-full bg-white/20" />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 pt-3">
            {/* The card itself — mirrors AnnouncementCard.tsx: 22px radius, accent
                gradient wash, inset rounded rail. */}
            <div
              className="relative flex-none overflow-hidden rounded-[18px] shadow-sm"
              style={{ backgroundColor: '#fff', border: `1px solid ${tint(accent, 0.16)}` }}
            >
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, ${tint(accent, 0.14)}, ${tint(accent, 0.05)} 45%, #FFFFFF)`,
                }}
              />

              {/* Static stand-in for the animated particles — enough to show placement
                  and density. Held at the same 55% the device applies, behind the text. */}
              {decoration !== 'none' && (
                <div className="pointer-events-none absolute inset-0 select-none" style={{ opacity: 0.55 }}>
                  {[8, 34, 58, 80, 93].map((left, i) => (
                    <span
                      key={i}
                      className="absolute"
                      style={{
                        left: `${left}%`,
                        top: `${10 + ((i * 29) % 70)}%`,
                        fontSize: `${[13, 10, 15, 11, 12][i]}px`,
                      }}
                    >
                      {emoji}
                    </span>
                  ))}
                </div>
              )}

              <div className="relative px-3.5 py-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-1 flex-shrink-0 self-stretch rounded-full"
                    style={{ backgroundColor: accent, minHeight: 34 }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-extrabold leading-tight text-gray-900">{shownTitle}</p>
                    <p
                      ref={bodyRef}
                      className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-[12.5px] leading-snug text-gray-600"
                    >
                      {body}
                    </p>
                    {bodyCut && (
                      <p className="mt-1 text-[11.5px] font-bold" style={{ color: readableAccent(accent) }}>
                        Read more
                      </p>
                    )}

                    {segments && (
                      <div className="mt-2.5 flex items-center">
                        {segments.map((seg, i) => (
                          <div key={seg.label} className="flex items-center">
                            {i > 0 && (
                              <span
                                className="mx-0.5 -mt-2 text-sm font-extrabold"
                                style={{ color: tint(readableAccent(accent), 0.4) }}
                              >
                                :
                              </span>
                            )}
                            <div
                              className="flex min-w-[38px] flex-col items-center rounded-[9px] px-1.5 py-1"
                              style={{ backgroundColor: tint(accent, 0.12) }}
                            >
                              <span
                                className="text-[15px] font-extrabold leading-none tabular-nums"
                                style={{ color: readableAccent(accent) }}
                              >
                                {seg.value}
                              </span>
                              <span
                                className="mt-0.5 text-[7.5px] font-extrabold tracking-wider"
                                style={{ color: tint(readableAccent(accent), 0.7) }}
                              >
                                {seg.label}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* The task's button — same place as on the device: under the text, lined
                    up with it past the rail, above any image. Filled with the readable accent. */}
                {ctaLabel && (
                  <div className="mt-3 flex flex-wrap gap-2 pl-4">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12.5px] font-bold"
                      style={{
                        backgroundColor: readableAccent(accent),
                        color: labelOnFill(readableAccent(accent)),
                      }}
                    >
                      {ctaLabel}
                      {ctaArrow && <ArrowRight className="h-3.5 w-3.5" />}
                    </span>
                    {secondaryLabel && (
                      <span
                        className="inline-flex items-center rounded-full border bg-white px-3.5 py-1.5 text-[12.5px] font-bold"
                        style={{ borderColor: readableAccent(accent), color: readableAccent(accent) }}
                      >
                        {secondaryLabel}
                      </span>
                    )}
                  </div>
                )}

                {image && (
                  <div className="relative mt-3 overflow-hidden rounded-[14px]">
                    <img src={image} alt="" className="h-[130px] w-full object-cover" />
                    <div className="absolute inset-0 rounded-[14px] border border-black/10" />
                    <div className="absolute bottom-1.5 right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-slate-900/45 text-[9px] text-white">
                      ⛶
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ghosts of the next cards down, so the announcement reads as sitting on top
                of a real screen. They fill what is left and never make the screen scroll;
                only a card too tall for the screen does. */}
            <div className="min-h-0 flex-1 overflow-hidden">
              {[56, 96, 96, 96].map((h, i) => (
                <div key={i} className="mt-2.5 rounded-[18px] border border-gray-200 bg-white/70" style={{ height: h }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {note && <p className="mt-1.5 flex-none text-center text-[11px] leading-[14px] text-gray-400">{note}</p>}
    </aside>
  );
}
